<script setup lang="ts">
/**
 * The helper in the corner of an assistant page: a button, a panel with
 * the transcript, an input. State comes from `useHelperChat`; the page
 * only says what to call it and what to suggest when it is empty.
 */
import { ref } from "vue";

import type { HelperChat } from "../composables/useHelperChat";
import MessageBubble from "./MessageBubble.vue";

defineProps<{
  helper: HelperChat;
  /** "Bid Writer", "Servicekatalog-hjælper" — who is answering. */
  title: string;
  /** What it can see: "ser det tilbud, du har på skærmen". */
  hint?: string;
  /** Shown before the first turn: what to ask. */
  empty?: string;
  devUser: string;
  /** No record to talk about yet. */
  disabled?: boolean;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
function onFiles(helper: HelperChat, e: Event): void {
  const el = e.target as HTMLInputElement;
  const files = el.files ? Array.from(el.files) : [];
  el.value = "";
  void helper.attach(files);
}
function onDrop(helper: HelperChat, e: DragEvent): void {
  if (!helper.uploadsEnabled) return;
  e.preventDefault();
  const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
  void helper.attach(files);
}
</script>

<template>
  <button
    type="button"
    class="helper__fab"
    :aria-expanded="helper.open.value"
    title="Spørg assistenten om det, der er på skærmen"
    @click="helper.open.value = !helper.open.value"
  >
    {{ helper.open.value ? "×" : "Hjælp" }}
  </button>
  <section
    v-if="helper.open.value"
    class="helper"
    aria-label="Assistent"
    @dragover.prevent
    @drop="onDrop(helper, $event)"
  >
    <header class="helper__head">
      <strong>{{ title }}</strong>
      <span v-if="hint" class="helper__hint">{{ hint }}</span>
    </header>
    <div class="helper__log">
      <p v-if="!helper.turns.length && empty" class="helper__hint">{{ empty }}</p>
      <MessageBubble
        v-for="t in helper.turns"
        :key="t.id"
        :turn="t"
        :conversation-id="helper.conversationId.value ?? undefined"
        :dev-user="devUser"
      />
    </div>
    <div v-if="helper.attached.value.length || helper.uploadStatus.value" class="helper__files">
      <span v-for="f in helper.attached.value" :key="f" class="helper__file">📎 {{ f }}</span>
      <span v-if="helper.uploadStatus.value" class="helper__hint">{{ helper.uploadStatus.value }}</span>
    </div>
    <form class="helper__input" @submit.prevent="helper.send()">
      <template v-if="helper.uploadsEnabled">
        <input
          ref="fileInput"
          type="file"
          class="helper__fileinput"
          multiple
          accept=".pdf,.docx,.doc,.txt,.log,.md,.json,.csv,.xlsx,.pptx,.png,.jpg,.jpeg"
          @change="onFiles(helper, $event)"
        />
        <button
          type="button"
          class="btn btn--small"
          title="Vedhæft en fil (eller træk den herind)"
          aria-label="Vedhæft en fil"
          :disabled="helper.busy.value || disabled"
          @click="fileInput?.click()"
        >
          📎
        </button>
      </template>
      <input
        v-model="helper.input.value"
        type="text"
        placeholder="Skriv til assistenten…"
        :disabled="helper.busy.value || disabled"
        aria-label="Besked"
      />
      <button
        type="submit"
        class="btn btn--primary btn--small"
        :disabled="helper.busy.value || disabled || !helper.input.value.trim()"
      >
        Send
      </button>
    </form>
  </section>
</template>

<style scoped>
.helper__fab { position: fixed; right: 20px; bottom: 20px; z-index: 60; border: none; border-radius: var(--radius-pill, 999px); padding: 12px 18px; background: var(--color-accent); color: var(--color-inverse); font: inherit; font-weight: 600; cursor: pointer; box-shadow: var(--shadow-lg, 0 8px 30px rgba(0, 0, 0, 0.18)); }
.helper { position: fixed; right: 20px; bottom: 72px; z-index: 60; width: min(440px, 94vw); height: min(560px, 80vh); display: flex; flex-direction: column; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg, 12px); box-shadow: var(--shadow-lg, 0 8px 30px rgba(0, 0, 0, 0.18)); overflow: hidden; }
.helper__head { padding: 10px 14px; border-bottom: 1px solid var(--color-border); display: flex; flex-direction: column; }
.helper__hint { color: var(--color-muted); font-size: 12.5px; }
.helper__log { flex: 1; overflow-y: auto; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px; }
.helper__input { display: flex; gap: 6px; padding: 10px; border-top: 1px solid var(--color-border); }
.helper__files { display: flex; flex-wrap: wrap; gap: 6px; padding: 6px 12px 0; }
.helper__file { font-size: 12px; padding: 2px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-pill, 999px); background: var(--color-surface-alt); }
.helper__fileinput { display: none; }
.helper__input input:not(.helper__fileinput) { flex: 1; padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md, 6px); background: var(--color-surface-alt); color: inherit; font: inherit; }
</style>
